import { createTheme, type PaletteMode, type Theme } from "@mui/material/styles";
import { T_LIGHT, T_DARK } from "../features/agents/tokens";

// Global MUI theme — consumes the Designer-handoff Style E tokens
// (`apps/web/src/features/agents/tokens.ts`) so the chat rail and the rest of
// the Manna surface share one palette, type scale, and motion system. Any
// token tweak there propagates here. Per HANDOFF §"Foundation rules":
//   • indigo `accent` is for chrome only
//   • forest `confirm` is for affirming actions only — never collapse the two
//   • status indicators (unread / pending / failed / success) sit on
//     matched chroma so dot+halo treatments stay legible across modes.

export function buildTheme(mode: PaletteMode): Theme {
  const T = mode === "dark" ? T_DARK : T_LIGHT;
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: T.accent,
        light: T.accentDim,
        dark: isDark ? "#5959C0" : "#3E3FB0",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: T.confirm,
        light: isDark ? "#7AC9A2" : "#4A8C72",
        dark: isDark ? "#3A8A66" : "#155039",
        contrastText: "#FFFFFF",
      },
      error: { main: T.failed, contrastText: "#FFFFFF" },
      warning: { main: T.pending, contrastText: "#FFFFFF" },
      info: { main: T.unread, contrastText: "#FFFFFF" },
      success: { main: T.success, contrastText: "#FFFFFF" },
      background: {
        default: T.bg,
        paper: T.rail,
      },
      text: {
        primary: T.text,
        secondary: T.textDim,
        disabled: T.textFaint,
      },
      divider: T.hair,
      action: {
        hover: T.panel2,
        selected: T.accentSoft,
        disabled: T.textFaint,
        disabledBackground: T.panel2,
      },
    },
    typography: {
      fontFamily: T.sans,
      // 6-step scale from D9. Designer expresses sizes as raw px; MUI is
      // happy with px strings. We keep relative letter-spacing tight on
      // headings to match the prototype (no airy display look).
      fontSize: T.fs_body,
      htmlFontSize: T.fs_body,
      // Page titles match the prototype "All deals" treatment
      // (proto-crm.jsx:116 — 20px / 600 / -0.3). h2 drops to 18 so the
      // hierarchy stays consistent.
      h1: { fontSize: 20, fontWeight: 600, letterSpacing: -0.3, lineHeight: 1.25 },
      h2: { fontSize: 18, fontWeight: 600, letterSpacing: -0.2, lineHeight: 1.3 },
      h3: { fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
      h4: { fontSize: T.fs_h, fontWeight: 600, lineHeight: 1.35 },
      h5: { fontSize: T.fs_title, fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: T.fs_label, fontWeight: 600, lineHeight: 1.4 },
      subtitle1: { fontSize: T.fs_title, fontWeight: 500 },
      subtitle2: { fontSize: T.fs_label, fontWeight: 500 },
      body1: { fontSize: T.fs_body, lineHeight: 1.5 },
      body2: { fontSize: T.fs_label, lineHeight: 1.5 },
      caption: { fontSize: T.fs_meta, color: T.textMute },
      overline: {
        fontFamily: T.mono,
        fontSize: T.fs_micro,
        fontWeight: 600,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: T.textMute,
      },
      button: {
        fontSize: T.fs_label,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
      },
    },
    shape: {
      borderRadius: T.r_md,
    },
    transitions: {
      duration: {
        shortest: T.d_micro,
        shorter: T.d_micro,
        short: T.d_short,
        standard: T.d_med,
        complex: T.d_long,
        enteringScreen: T.d_med,
        leavingScreen: T.d_short,
      },
      easing: {
        easeInOut: T.ease_in_out,
        easeOut: T.ease_out,
        easeIn: T.ease_in_out,
        sharp: T.ease_emph,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: T.bg,
            color: T.text,
            fontFamily: T.sans,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          code: { fontFamily: T.mono },
          kbd: { fontFamily: T.mono },
          pre: { fontFamily: T.mono },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            borderRadius: T.r_md,
          },
          // Per HANDOFF foundation rule: accent is for chrome only. Primary
          // CTAs use neutral text-color fill (prototype proto-crm.jsx:175
          // "+ New deal" — bg=T.text, color=T.rail). The hover swap keeps
          // the contrast inversion legible across modes.
          containedPrimary: {
            backgroundColor: T.text,
            color: T.rail,
            "&:hover": { backgroundColor: isDark ? "#FFFFFF" : "#1C1F25" },
          },
          outlinedPrimary: {
            borderColor: T.accentBorder,
            color: T.accent,
            "&:hover": { backgroundColor: T.accentSoft, borderColor: T.accent },
          },
          textPrimary: {
            color: T.accent,
            "&:hover": { backgroundColor: T.accentSoft },
          },
          containedSecondary: {
            backgroundColor: T.confirm,
            color: "#FFFFFF",
            "&:hover": { backgroundColor: isDark ? "#71C49C" : "#185A41" },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${T.hair}`,
            backgroundColor: T.rail,
            color: T.text,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: T.rail,
            borderColor: T.hair,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: T.rail,
          },
          outlined: {
            borderColor: T.hair,
          },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: {
            borderColor: T.hair,
            backgroundColor: T.rail,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: T.hair,
            fontSize: T.fs_label,
          },
          head: {
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            fontSize: T.fs_micro,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            backgroundColor: T.panel,
            borderBottomColor: T.hair2,
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small", fullWidth: true },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: T.r_md,
            backgroundColor: T.rail,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: T.hair2 },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.accentBorder },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: T.accent,
              borderWidth: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: T.textDim,
            fontSize: T.fs_label,
            "&.Mui-focused": { color: T.accent },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: T.fs_meta,
            fontFamily: T.sans,
            borderRadius: T.r_sm,
          },
          outlined: { borderColor: T.hair2 },
          colorPrimary: {
            backgroundColor: T.accentSoft,
            color: T.accent,
            borderColor: T.accentBorder,
          },
          colorSuccess: {
            backgroundColor: T.confirmSoft,
            color: T.confirm,
            borderColor: T.confirmBorder,
          },
          colorWarning: {
            backgroundColor: T.pendingSoft,
            color: T.pending,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            fontSize: T.fs_label,
            minHeight: 40,
            color: T.textDim,
            "&.Mui-selected": { color: T.accent },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: T.accent, height: 2 },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: T.hair },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: T.fs_meta,
            backgroundColor: isDark ? "#1C1F25" : "#0F1115",
            color: "#FFFFFF",
            borderRadius: T.r_sm,
            paddingInline: 8,
          },
          arrow: { color: isDark ? "#1C1F25" : "#0F1115" },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: T.r_md, fontSize: T.fs_label },
          standardError: { backgroundColor: isDark ? "#3A1A1C" : "#FDECEC" },
          standardWarning: { backgroundColor: T.pendingSoft, color: T.pending },
          standardSuccess: { backgroundColor: T.confirmSoft, color: T.confirm },
          standardInfo: { backgroundColor: T.accentSoft, color: T.accent },
        },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: {
          root: { color: T.accent },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          // Per HANDOFF foundation rule: accent is for chrome only. Active
          // nav uses neutral panel2 + text (prototype CRMSideNav, proto-
          // crm.jsx:68-70) — NOT accentSoft. fontWeight 500 active vs
          // inherited 400 inactive matches the prototype.
          root: {
            borderRadius: T.r_sm,
            color: T.textDim,
            "&:hover": { backgroundColor: T.panel2 },
            "&.Mui-selected": {
              backgroundColor: T.panel2,
              color: T.text,
              fontWeight: 500,
              "& .MuiListItemIcon-root": { color: T.text },
              "&:hover": { backgroundColor: T.panel2 },
            },
            "& .MuiListItemIcon-root": { color: T.textDim },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: T.fs_label,
            "&.Mui-selected": { backgroundColor: T.panel2, color: T.text },
          },
        },
      },
      MuiCircularProgress: {
        defaultProps: { "aria-label": "Loading" },
      },
      MuiLinearProgress: {
        defaultProps: { "aria-label": "Loading" },
      },
    },
  });
}
